import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';

import {
    LegacyCityPropType,
    useSession,
    useIsMounted,
} from '@ratehub/base-ui';
import {
    DEPOSITS_VERTICALS,
    fetchProductTableBestGICRatesDefaults,
    trackHeapEventForDepositsAndNavigate,
} from '@ratehub/inv-common';

import PAGE_TEMPLATES from '../definitions/PageTemplates';
import GICsAccountPropType from '../definitions/GICsAccountPropType';
import ProductTableBestGICRatesTable from './ProductTableBestGICRatesTable';
import ProductTableHeader, { PRODUCT_TABLE_TITLE_ELEMENT_TYPES } from './ProductTableHeader';


function ProductTableBestGICRates({
    title,
    titleAs = 'h2',
    initialLastUpdatedDate,

    sponsoredGIC,
    initialRates = [],
    initialFilterValues,
    initialProvince,

    ...otherProps
}) {
    const { interestLocation } = useSession();
    const intl = useIntl();

    const [ rates, setRates ] = useState(initialRates);

    const [ lastUpdatedDate, setLastUpdatedDate ] = useState(initialLastUpdatedDate);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ provinceCode, setProvinceCode ] = useState(initialProvince);
    const getIsMounted = useIsMounted();

    // re-fetch the rates when the component mounts with auto-detected user location
    useEffect(() => {
        if (interestLocation?.province) {
            setProvinceCode(interestLocation.province);
            fetchProductTableBestGICRatesDefaults(interestLocation.province, intl.locale)
                .then((response) => {
                    if (getIsMounted()) {
                        setRates(response.rates);

                        setLastUpdatedDate(response.lastUpdatedDate);
                        setIsLoading(false);
                    }
                });
        } else {
            setIsLoading(false);
        }
    }, [ interestLocation?.province, isLoading ]);


    // TODO for use in FilteredAsyncContainer
    // do things with initialFilterValues (and assign a default if necessary)

    function handleRateClick(rate) {
        trackHeapEventForDepositsAndNavigate({
            product: rate,
            shouldNavigate: true,
            provinceCode,
            vertical: DEPOSITS_VERTICALS.GIC,
        });
    }

    return (
        <div {...otherProps}>
            <If condition={title || lastUpdatedDate}>
                <ProductTableHeader
                    title={title}
                    titleAs={titleAs}
                    lastUpdatedDate={lastUpdatedDate}
                />
            </If>

            <ProductTableBestGICRatesTable
                isLoading={isLoading}
                sponsoredGIC={sponsoredGIC}
                rates={rates}
                onRateClick={handleRateClick}
            />
        </div>
    );
}

ProductTableBestGICRates.propTypes = {
    title: PropTypes.string,
    titleAs: PropTypes.oneOf(PRODUCT_TABLE_TITLE_ELEMENT_TYPES),
    initialLastUpdatedDate: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
    ]),

    sponsoredGIC: GICsAccountPropType,
    initialRates: PropTypes.arrayOf(GICsAccountPropType),

    initialProvince: PropTypes.string,

    initialFilterValues: PropTypes.shape({
        province: PropTypes.string,
        city: LegacyCityPropType,
    }),
};


ProductTableBestGICRates.blockKey = 'rh/product-table-best-gic-rates';
ProductTableBestGICRates.requiresLayoutRow = true;
ProductTableBestGICRates.supportedPageTypes = [ PAGE_TEMPLATES.gics, PAGE_TEMPLATES.sandbox ];

export default observer(ProductTableBestGICRates);
